import { FC, useMemo } from 'react';
import { CloseRounded, OpenInNewRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { FileView } from 'api';
import { FileModel } from 'api';
import { DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS } from 'components/Drawer/document-preview-drawer-tab/document-preview.constants';
import ImageViewer from 'components/FileViewer/image-viewer/ImageViewer';
import PDFViewer from 'components/FileViewer/pdf-viewer/PDFViewer';
import { PDFViewerViewMode } from 'components/FileViewer/pdf-viewer/PDFViewer.props';
import { useDocuments } from 'services/hooks';
import { OnboardingEmailDocumentPreviewDialogProps } from './OnboardingEmailDocumentPreviewDialog.props';

const IMAGE_FILE_TYPES = [FileModel.file_type.PNG, FileModel.file_type.JPEG, FileModel.file_type.JPG];

const OnboardingEmailDocumentPreviewDialog: FC<OnboardingEmailDocumentPreviewDialogProps> = ({
  open,
  file,
  onClose,
  firmId,
}) => {
  const { downloadDocumentContent, downloadDocument } = useDocuments();

  const fileType = file?.file_type;
  const isPdf = fileType === FileModel.file_type.PDF;
  const isImage = Boolean(fileType && IMAGE_FILE_TYPES.includes(fileType));
  const isInlinePreviewable = isPdf || isImage;

  // FileModel and FileView share the fields downloadDocumentContent reads
  // (id, filename, file_type); adapt to the FileData shape via a narrow cast.
  const fileData = useMemo(() => (file ? { file: file as unknown as FileView, firmId } : null), [file, firmId]);

  const { data, isLoading } = useQuery({
    queryKey: ['onboarding-email-preview', file?.id, 'content'],
    enabled: open && Boolean(file?.id) && isInlinePreviewable && Boolean(fileData),
    staleTime: DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS,
    queryFn: () => downloadDocumentContent(fileData!, { fallbackToSignedUrl: true }),
  });

  const title = file ? `${file.filename}.${file.file_type}` : 'Preview';

  const handleOpenInNewTab = () => {
    if (fileData) {
      downloadDocument(fileData);
    }
  };

  const renderBody = () => {
    if (!file) {
      return null;
    }
    if (!isInlinePreviewable) {
      return (
        <Stack spacing={2} sx={{ alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            This file type can&apos;t be previewed here.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<OpenInNewRounded />}
            onClick={handleOpenInNewTab}
            id="onboarding-preview-open-new-tab">
            Open in new tab
          </Button>
        </Stack>
      );
    }
    if (isLoading) {
      return <LinearProgress />;
    }
    if (!data?.content && !data?.blob_url) {
      return (
        <Stack spacing={2} sx={{ alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            Unable to load a preview for this document.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<OpenInNewRounded />}
            onClick={handleOpenInNewTab}
            id="onboarding-preview-open-new-tab">
            Open in new tab
          </Button>
        </Stack>
      );
    }
    if (isPdf) {
      return (
        <PDFViewer base64Content={data.content} src={data.blob_url} viewMode={PDFViewerViewMode.DOCUMENT} />
      );
    }
    return <ImageViewer base64Content={data.content} src={data.blob_url} compensateAppZoom />;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      id="onboarding-email-document-preview-dialog"
      data-testid="onboarding-email-document-preview-dialog">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <Typography variant="subtitle1" noWrap sx={{ flex: 1 }}>
          {title}
        </Typography>
        <IconButton
          aria-label="Close preview"
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
          id="onboarding-email-document-preview-close">
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ height: '75vh', minHeight: 0, display: 'flex', flexDirection: 'column' }}>{renderBody()}</Box>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingEmailDocumentPreviewDialog;
